import React, { useEffect } from 'react';
import { Box, Typography, IconButton, useTheme, SxProps } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { TrashIcon } from 'components/Icons/TrashIcon';
import { ProgressBar } from 'components/ProgressBar';
import { formatBytes } from 'core/services/storage/utils';
import { S3Bucket } from 'core/services/amplify/config/constants';
import { useAmplifyUploadData } from 'core/services/amplify/storage/useAmplifyUploadFile';

const cardStyles = {
  borderRadius: 2,
  border: 1,
  mb: 2,
  display: 'flex',
  justifyContent: 'space-between',
  height: 75,
  p: 2
};

const contentStyles = {
  flex: '1 0 auto'
};

const iconsStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginLeft: 1
};

type Props = {
  file: File;
  onComplete: (key: string) => void;
  onDelete: (path: string | undefined) => void;
};

const UploadedFileItem: React.FC<Omit<Props, 'onComplete'>> = ({
  file,
  onDelete
}) => {
  const {
    palette: { customGrey }
  } = useTheme();

  const handleDeleteClick = () => {
    onDelete(file.name);
  };

  return (
    <Box sx={cardStyles} borderColor={customGrey.light}>
      <Box sx={contentStyles}>
        <Typography variant="body2" fontWeight="bold">
          {file.name}
        </Typography>
        <Typography variant="body2">{formatBytes(file.size)}</Typography>
      </Box>
      <Box sx={iconsStyles}>
        <Box>
          <IconButton aria-label="delete" onClick={handleDeleteClick}>
            <TrashIcon />
          </IconButton>
        </Box>
        <Box height={20}>
          <CheckCircleIcon
            sx={{ color: 'success.contrastText' }}
            fontSize="small"
          />
        </Box>
      </Box>
    </Box>
  );
};

const UploadFileItem: React.FC<Props> = ({ file, onComplete, onDelete }) => {
  const {
    palette: { customGrey, success }
  } = useTheme();

  const {
    uploadResult,
    uploadProgress,
    isUploading,
    uploadData,
    cancelUpload
  } = useAmplifyUploadData({
    bucket: S3Bucket.CANDIDATE_BATCH
  });

  useEffect(() => {
    const upload = async () => {
      if (uploadResult || isUploading) return;

      const result = await uploadData({ file });
      if (result) {
        onComplete(result.path);
      }
    };
    upload();
  }, [uploadResult, isUploading]);

  const handleDeleteClick = () => {
    cancelUpload();
    onDelete(uploadResult?.path);
  };

  return (
    <Box
      sx={{
        ...cardStyles,
        borderColor: uploadResult ? success.contrastText : customGrey.light
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={contentStyles}>
          <Typography variant="body2" fontWeight="bold">
            {file.name}
          </Typography>
          <Typography variant="body2">{formatBytes(file.size)}</Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <ProgressBar progress={uploadProgress} />
        </Box>
      </Box>
      <Box sx={iconsStyles}>
        <Box>
          <IconButton aria-label="delete" onClick={handleDeleteClick}>
            <TrashIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export { UploadedFileItem, UploadFileItem };
